import React, { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import {
  ReactFlow, Background, Controls, MiniMap, Panel,
  OnNodeDrag, ReactFlowProvider, NodeMouseHandler, Connection, Edge,
  useOnSelectionChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useDiagramStore } from './store/diagramStore';
import LaneNode from './nodes/LaneNode';
import StepNode from './nodes/StepNode';
import FlowEdge from './edges/FlowEdge';
import ScenarioPanel from './panels/ScenarioPanel';
import PartnerPanel from './panels/PartnerPanel';
import ToolbarPanel from './panels/ToolbarPanel';
import CollisionPanel from './panels/CollisionPanel';

const nodeTypes = { laneNode: LaneNode, stepNode: StepNode };
const edgeTypes = { flowEdge: FlowEdge };

/* ─── Toggle Button (like Service Map) ─── */
const ToggleBtn: React.FC<{ isOpen: boolean; onClick: () => void; side: 'left' | 'right' }> = (
  { isOpen, onClick, side },
) => (
  <button
    onClick={onClick}
    title={isOpen ? 'Свернуть панель' : 'Развернуть панель'}
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 24, height: 24, borderRadius: 4,
      border: '1px solid #E0E0E0', background: '#fff',
      cursor: 'pointer', color: '#757575', fontSize: 12,
      flexShrink: 0,
    }}
  >
    {side === 'left' ? (isOpen ? '«' : '»') : (isOpen ? '»' : '«')}
  </button>
);

const DiagramsPageInner: React.FC = () => {
  const nodes = useDiagramStore(s => s.nodes);
  const edges = useDiagramStore(s => s.edges);
  const onNodesChange = useDiagramStore(s => s.onNodesChange);
  const onEdgesChange = useDiagramStore(s => s.onEdgesChange);
  const updateNodePosition = useDiagramStore(s => s.updateNodePosition);
  const loadFromApi = useDiagramStore(s => s.loadFromApi);
  const reactFlowRef = useRef<HTMLDivElement>(null);

  const [leftOpen, setLeftOpen] = useState(true);
  const [partnersOpen, setPartnersOpen] = useState(true);

  // Load scenarios from API on mount
  useEffect(() => {
    loadFromApi();
  }, [loadFromApi]);

  const toggleScenario = useDiagramStore(s => s.toggleScenario);
  const addConnection = useDiagramStore(s => s.addConnection);
  const removeConnection = useDiagramStore(s => s.removeConnection);

  // Track selected edges via React Flow hook
  const [selectedEdgeIds, setSelectedEdgeIds] = useState<string[]>([]);
  useOnSelectionChange({
    onChange: ({ edges }) => setSelectedEdgeIds(edges.map(e => e.id)),
  });

  const handleDeleteSelectedEdges = useCallback(() => {
    selectedEdgeIds.forEach(id => removeConnection(id));
    setSelectedEdgeIds([]);
  }, [selectedEdgeIds, removeConnection]);

  const reconnectConnection = useDiagramStore(s => s.reconnectConnection);
  const edgeReconnecting = useRef(false);

  const onReconnectStart = useCallback(() => {
    edgeReconnecting.current = true;
  }, []);

  const onReconnect = useCallback((oldEdge: Edge, newConnection: Connection) => {
    edgeReconnecting.current = false;
    reconnectConnection(oldEdge.id, newConnection);
  }, [reconnectConnection]);

  const onReconnectEnd = useCallback((_: MouseEvent | TouchEvent, edge: Edge) => {
    if (edgeReconnecting.current) {
      // Dropped without connecting — remove the edge
      removeConnection(edge.id);
      edgeReconnecting.current = false;
    }
  }, [removeConnection]);

  const onConnect = useCallback((connection: Connection) => {
    const { scenarios } = useDiagramStore.getState();
    const sourceStepId = connection.source?.replace(/^step-/, '') ?? '';
    const scenario = scenarios.find(sc => sc.diagram.steps.some(s => s.id === sourceStepId));
    addConnection(connection, scenario?.color ?? '#888', scenario?.id ?? '');
  }, [addConnection]);

  const onNodeDragStop: OnNodeDrag = useCallback((_event, node) => {
    updateNodePosition(node.id, node.position);
  }, [updateNodePosition]);

  // Click on collapsed node → expand scenario
  const onNodeClick: NodeMouseHandler = useCallback((_event, node) => {
    if (node.id.startsWith('step-collapsed-')) {
      const scenarioId = node.id.replace('step-collapsed-', '');
      toggleScenario(scenarioId);
    }
  }, [toggleScenario]);

  const defaultEdgeOptions = useMemo(() => ({
    type: 'flowEdge',
    markerEnd: { type: 'arrowclosed' as const, color: '#888' },
  }), []);

  const handleExportPdf = useCallback(async () => {
    const el = reactFlowRef.current?.querySelector('.react-flow__viewport') as HTMLElement | null;
    if (!el) return;

    try {
      // @ts-ignore
      const html2canvas = (await import('html2canvas')).default;
      // @ts-ignore
      const { jsPDF } = await import('jspdf');

      // High resolution capture
      const canvas = await html2canvas(el, {
        backgroundColor: '#fafafa',
        scale: 3,
        useCORS: true,
      });

      const imgData = canvas.toDataURL('image/png');
      const imgW = canvas.width;
      const imgH = canvas.height;

      // PDF size matches content (width = zone width)
      const pdfW = imgW / 3; // scale back from 3x
      const pdfH = imgH / 3;
      const margin = 10;

      const pdf = new jsPDF({
        orientation: pdfW > pdfH ? 'landscape' : 'portrait',
        unit: 'px',
        format: [pdfW + margin * 2, pdfH + margin * 2],
      });

      pdf.addImage(imgData, 'PNG', margin, margin, pdfW, pdfH);
      pdf.save(`diagrams-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch {
      window.print();
    }
  }, []);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 56px)',
      overflow: 'hidden',
      background: '#F8F9FA',
    }}>
      {/* Toolbar — fixed top, 48px */}
      <ToolbarPanel onExportPdf={handleExportPdf} />
      <CollisionPanel />

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left sidebar — collapsible */}
        {leftOpen ? (
          <div style={{
            width: 260, minWidth: 260, height: '100%',
            background: '#fff',
            borderRight: '1px solid #E0E0E0',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 10,
          }}>
            <div className="d-flex align-items-center justify-content-between px-2 py-1"
              style={{ borderBottom: '1px solid #E0E0E0', flexShrink: 0 }}>
              <small className="fw-bold">Сценарии</small>
              <ToggleBtn isOpen side="left" onClick={() => setLeftOpen(false)} />
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <ScenarioPanel />
            </div>

            {/* Partners section */}
            <div style={{ borderTop: '2px solid #E0E0E0', flexShrink: 0 }}>
              <div
                className="d-flex align-items-center justify-content-between px-2 py-1"
                style={{ cursor: 'pointer', background: '#fafafa' }}
                onClick={() => setPartnersOpen(o => !o)}
              >
                <small className="fw-bold text-muted" style={{ fontSize: '0.75rem', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  Партнёры
                </small>
                <span style={{ fontSize: 9, color: '#aaa' }}>{partnersOpen ? '▲' : '▼'}</span>
              </div>
              {partnersOpen && <PartnerPanel />}
            </div>
          </div>
        ) : (
          <div style={{
            width: 36, minWidth: 36, height: '100%',
            background: '#fff',
            borderRight: '1px solid #E0E0E0',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', padding: '10px 0', gap: 8,
            zIndex: 10,
          }}>
            <ToggleBtn isOpen={false} side="left" onClick={() => setLeftOpen(true)} />
            {/* Collapsed: show scenario color dots */}
            {useDiagramStore.getState().scenarios.map(sc => (
              <div
                key={sc.id}
                title={sc.title}
                style={{
                  width: 14, height: 14, borderRadius: '50%',
                  background: sc.color,
                  opacity: sc.visible ? 1 : 0.3,
                  cursor: 'pointer',
                  border: '1px solid #ccc',
                }}
                onClick={() => useDiagramStore.getState().toggleScenario(sc.id)}
              />
            ))}
          </div>
        )}

        {/* Main canvas */}
        <div ref={reactFlowRef} style={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeDragStop={onNodeDragStop}
            onNodeClick={onNodeClick}
            onConnect={onConnect}
            onReconnect={onReconnect}
            onReconnectStart={onReconnectStart}
            onReconnectEnd={onReconnectEnd}
            defaultEdgeOptions={defaultEdgeOptions}
            fitView
            fitViewOptions={{ padding: 0.15 }}
            minZoom={0.05}
            maxZoom={3}
            deleteKeyCode="Delete"
            snapToGrid
            snapGrid={[10, 10]}
          >
            {selectedEdgeIds.length > 0 && (
              <Panel position="top-center">
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: '#fff', border: '1px solid #E0E0E0',
                  borderRadius: 6, padding: '5px 10px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  fontSize: '0.8rem',
                }}>
                  <span style={{ color: '#666' }}>
                    Выбрано связей: {selectedEdgeIds.length}
                  </span>
                  <button
                    onClick={handleDeleteSelectedEdges}
                    style={{
                      background: '#fff0f0', border: '1px solid #f44336',
                      borderRadius: 4, color: '#f44336',
                      fontSize: '0.75rem', padding: '2px 10px',
                      cursor: 'pointer', fontWeight: 600,
                    }}
                  >
                    Удалить
                  </button>
                </div>
              </Panel>
            )}

            <Background gap={20} size={1} color="#E8E8E8" />
            <Controls
              style={{ bottom: 16, right: 16, left: 'auto' }}
              showInteractive={false}
            />
            <MiniMap
              nodeColor={(n) => {
                if (n.type === 'laneNode') return (n.data as Record<string, unknown>).color as string || '#eee';
                return (n.data as Record<string, unknown>).scenarioColor as string || '#888';
              }}
              maskColor="rgba(0,0,0,0.08)"
              style={{ bottom: 16, left: 16 }}
            />
          </ReactFlow>
        </div>
      </div>
    </div>
  );
};

const DiagramsPage: React.FC = () => (
  <ReactFlowProvider>
    <DiagramsPageInner />
  </ReactFlowProvider>
);

export default DiagramsPage;
